import React, { ComponentType, useCallback, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import type {} from "chartjs-plugin-datalabels";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import {
  formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import {
  formatCompactCurrencyTick,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import { useGetFinancialsRedeemableDaily } from "../../../../queries/financials/useGetFinancialsRedeemableDaily";
import { DATE_RANGE } from "../../../../utils/Constants";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";

import styles from "./RedeemableBalance.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const BAR_COLOR = "#EC4899";

interface RedeemableBalanceProps {
  isApiRunning?: boolean;
}

const RedeemableBalance: React.FC<RedeemableBalanceProps> = ({
  isApiRunning = false,
}) => {
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.subtract(29, "day").startOf("day"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.LAST_30_DAYS),
  );

  const currencySymbol = useCurrencySymbol() ?? "$";

  const dateFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data: points = [], isFetching, isError } =
    useGetFinancialsRedeemableDaily(dateFilters);

  // Legacy DatePicker contract — refetch is driven by `dates` -> query key.
  const getData = useCallback((_queryString: string | null) => undefined, []);

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => formatDateNoTimezone(p.date, "MMM D")),
      datasets: [
        {
          label: AppStrings.REDEEMABLE_BALANCE,
          data: points.map((p) => p.value),
          backgroundColor: BAR_COLOR,
          borderRadius: 3,
          maxBarThickness: 18,
        },
      ],
    }),
    [points],
  );

  const chartOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#9aa3b1",
            font: { size: 11 },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(255,255,255,0.06)" },
          ticks: {
            color: "#9aa3b1",
            font: { size: 11 },
            callback: (value: number | string) =>
              formatCompactCurrencyTick(Number(value), currencySymbol),
          },
        },
      },
      plugins: {
        legend: { display: false },
        title: { display: false },
        datalabels: { display: false },
        tooltip: {
          enabled: true,
          mode: "index" as const,
          intersect: false,
          backgroundColor: "#252729",
          padding: 12,
          displayColors: true,
          titleFont: { family: "Poppins-regular", size: 13, weight: "bold" },
          bodyFont: { family: "Poppins-regular", size: 13 },
          callbacks: {
            title: (items: { dataIndex: number }[]) => {
              if (!items?.length) return "";
              const iso = points[items[0].dataIndex]?.date;
              return iso ? formatDateNoTimezone(iso, "MMM D, YYYY") : "";
            },
            label: (item: { raw: unknown }) =>
              formatCurrency(Number(item.raw), currencySymbol),
          },
        },
      },
    }),
    [points, currencySymbol],
  );

  const exportRows = useMemo(
    () =>
      points.map((p) => ({
        date: p.date,
        redeemable_cash: p.value,
      })),
    [points],
  );

  const showLoading = isApiRunning || isFetching;

  return (
    <section className={styles.card} aria-label="Redeemable Balance">
      <header className={styles.header}>
        <h2 className={styles.title}>{AppStrings.REDEEMABLE_BALANCE}</h2>
        <div className={styles.controls}>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="redeemable-balance.xlsx"
          />
        </div>
      </header>

      <div className={styles.chartContainer}>
        {showLoading ? (
          <div className={styles.loaderOverlay} data-testid="chart-loader">
            <RebetLoader width={100} height={100} />
          </div>
        ) : isError ? (
          <p
            className={styles.errorBanner}
            role="alert"
            data-testid="redeemable-balance-error"
          >
            {AppStrings.FINANCIALS_REDEEMABLE_LOAD_ERROR}
          </p>
        ) : points.length === 0 ? (
          <p className={styles.emptyMessage} data-testid="redeemable-balance-empty">
            {AppStrings.FINANCIALS_REDEEMABLE_EMPTY_STATE}
          </p>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </div>
    </section>
  );
};

export default RedeemableBalance;
